import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const COL_BG   = "#1A1A2E";
const ACCENT   = "#ED9519";

/** Badge de condición — letra B / M / E con fondo de color */
function condSym(cond) {
  if (cond === "Buena")     return { text: "B", bold: true, fontSize: 7, color: "#fff", fillColor: "#2E7D32", alignment: "center", margin: [1,2,1,2] };
  if (cond === "Mala")      return { text: "M", bold: true, fontSize: 7, color: "#fff", fillColor: "#C62828", alignment: "center", margin: [1,2,1,2] };
  if (cond === "Efectuada") return { text: "E", bold: true, fontSize: 7, color: "#fff", fillColor: ACCENT,    alignment: "center", margin: [1,2,1,2] };
  return { text: "—", fontSize: 7, color: "#aaa", alignment: "center", margin: [1,2,1,2] };
}

/** Celda de cabecera de sección */
function secHeader(text) {
  return [{
    text: text.toUpperCase(),
    bold: true, fontSize: 7, color: "#fff",
    fillColor: "#555555",
    colSpan: 2,
    margin: [4, 3, 4, 3],
  }, {}];
}

/** Fila de item de checklist */
function itemRow(item, even) {
  return [
    {
      text: item.descripcion,
      fontSize: 7,
      color: "#222",
      fillColor: even ? "#f8f8f8" : "#fff",
      margin: [4, 2, 4, 2],
    },
    condSym(item.condicion),   // mantiene su propio fillColor (B/M/E)
  ];
}

/** Construye el body de una tabla de secciones */
function buildChecklistBody(seccionesKeys, secciones) {
  const body = [];
  seccionesKeys.forEach(key => {
    body.push(secHeader(key));
    secciones[key].forEach((item, i) => body.push(itemRow(item, i % 2 === 0)));
  });
  return body;
}

// ─── Función principal ────────────────────────────────────────────────────────

export function generarPdfReporte(s) {
  const imageUrl = require("../../Img/IronLogo2.png");

  // Agrupar condiciones por sección
  const condiciones = s.condicionesPorServicios || [];
  const secciones   = {};
  condiciones.forEach(item => {
    if (!secciones[item.seccion]) secciones[item.seccion] = [];
    secciones[item.seccion].push(item);
  });
  const secKeys = Object.keys(secciones);
  const mitad   = Math.ceil(secKeys.length / 2);
  const keysIzq = secKeys.slice(0, mitad);
  const keysDer = secKeys.slice(mitad);

  // Servicio seleccionado
  const SERVICE_TYPES = [
    { key: "R", label: "Recibe"            },
    { key: "P", label: "Pre\nEntrega"      },
    { key: "E", label: "Entrega"           },
    { key: "1", label: "1er\nServicio"     },
    { key: "2", label: "2do\nServicio"     },
    { key: "3", label: "3er\nServicio"     },
    { key: "4", label: "4to\nServicio"     },
    { key: "5", label: "5to\nServicio"     },
    { key: "U", label: "Entrega\nusado"    },
    { key: "X", label: "Entrega\nAdicional"},
  ];

  const tipoLabel = (s.tipoReporte || "").toUpperCase();

  // Checklist table builder
  const checkTable = (keys) => keys.length === 0 ? { text: "" } : ({
    table: {
      widths: ["*", 18],
      body: buildChecklistBody(keys, secciones),
    },
    layout: {
      hLineWidth: () => 0.3,
      vLineWidth: () => 0,
      hLineColor: () => "#e0e0e0",
      paddingLeft:  () => 0,
      paddingRight: () => 0,
      paddingTop:   () => 0,
      paddingBottom:() => 0,
    },
    margin: [0, 0, 0, 0],
  });

  // Transmisión label
  const transmLabel = s.tipoTransmision === "M" ? "T/C" : s.tipoTransmision === "C" ? "T/M" : "T/C";

  const doc = {
    pageSize:    "A4",
    pageMargins: [18, 18, 18, 18],
    content: [
      // ── 1. Header ──────────────────────────────────────────────────────────
      {
        columns: [
          { image: imageUrl, width: 70, margin: [0, 0, 8, 0] },
          {
            stack: [
              {
                table: {
                  widths: ["*", "auto"],
                  body: [[
                    { text: `REPORTE DE SERVICIO | ${tipoLabel}`, bold: true, fontSize: 11, color: COL_BG, fillColor: ACCENT, margin: [8, 5, 8, 5] },
                    { text: `O.T. Nº  ${s.id || ""}`, bold: true, fontSize: 10, color: COL_BG, fillColor: "#fff", alignment: "right", margin: [8, 5, 8, 5] },
                  ]],
                },
                layout: "noBorders",
              },
            ],
          },
        ],
        margin: [0, 0, 0, 6],
      },

      // ── 2. Cliente / Ubicación ────────────────────────────────────────────
      {
        table: {
          widths: ["auto", "*", "auto", "*"],
          body: [[
            { text: "CLIENTE", bold: true, fontSize: 7, color: "#fff", fillColor: COL_BG, margin: [4,3,4,3] },
            { text: s.cliente || "—", fontSize: 8, margin: [4,3,4,3] },
            { text: "UBICACIÓN EQUIPO", bold: true, fontSize: 7, color: "#fff", fillColor: COL_BG, margin: [4,3,4,3] },
            { text: s.ubicacion || "—", fontSize: 8, margin: [4,3,4,3] },
          ]],
        },
        layout: { hLineWidth: () => 0.3, vLineWidth: () => 0.3, hLineColor: () => "#ccc", vLineColor: () => "#ccc" },
        margin: [0, 0, 0, 4],
      },

      // ── 3. Tabla de servicio (Modelo / Serie / condición) ─────────────────
      {
        table: {
          widths: [60, 50, ...SERVICE_TYPES.map(() => "*")],
          headerRows: 1,
          body: [
            // Headers
            [
              { text: "Modelo",   bold: true, fontSize: 7, color: "#fff", fillColor: COL_BG, margin: [3,3,3,3] },
              { text: "Serie Nº", bold: true, fontSize: 7, color: "#fff", fillColor: COL_BG, margin: [3,3,3,3] },
              ...SERVICE_TYPES.map(t => ({
                text: t.label, bold: true, fontSize: 6,
                color: "#fff",
                fillColor: s.tipo === t.key ? ACCENT : COL_BG,
                alignment: "center", margin: [1,2,1,2],
              })),
            ],
            // Values
            [
              { text: s.modelo || "—", fontSize: 7, margin: [3,3,3,3] },
              { text: s.serie  || "—", fontSize: 7, margin: [3,3,3,3] },
              ...SERVICE_TYPES.map(t => ({
                text: s.tipo === t.key ? "●" : "",
                color: ACCENT, bold: true, fontSize: 10,
                alignment: "center", margin: [1,1,1,1],
              })),
            ],
          ],
        },
        layout: { hLineWidth: () => 0.3, vLineWidth: () => 0.3, hLineColor: () => "#ccc", vLineColor: () => "#ccc" },
        margin: [0, 0, 0, 3],
      },

      // ── 4. Datos del motor / T/C / T/M ───────────────────────────────────
      {
        table: {
          widths: ["auto", "*", "auto", "*", "auto", "auto", "auto", "*"],
          body: [
            [
              { text: "Modelo\nMotor",    bold: true, fontSize: 6.5, color: "#fff", fillColor: COL_BG, margin: [3,3,3,3] },
              { text: s.modeloMotor || "—", fontSize: 7, margin: [3,3,3,3] },
              { text: `Modelo\n${transmLabel}`, bold: true, fontSize: 6.5, color: "#fff", fillColor: COL_BG, margin: [3,3,3,3] },
              { text: s.modeloMotor1 || "—", fontSize: 7, margin: [3,3,3,3] },
              { text: "Nº Serie",    bold: true, fontSize: 6.5, color: "#fff", fillColor: COL_BG, margin: [3,3,3,3] },
              { text: "DD/MM/AA",   bold: true, fontSize: 6.5, color: "#fff", fillColor: COL_BG, margin: [3,3,3,3] },
              { text: "Horómetro",  bold: true, fontSize: 6.5, color: "#fff", fillColor: COL_BG, margin: [3,3,3,3] },
              { text: String(s.horometro || "—"), fontSize: 7, margin: [3,3,3,3] },
            ],
            [
              { text: "Serie\nMotor",     bold: true, fontSize: 6.5, color: "#fff", fillColor: COL_BG, margin: [3,3,3,3] },
              { text: s.serieMotor || "—", fontSize: 7, margin: [3,3,3,3] },
              { text: `Nº Serie\n${transmLabel}`, bold: true, fontSize: 6.5, color: "#fff", fillColor: COL_BG, margin: [3,3,3,3] },
              { text: s.serieMotor1 || "—", fontSize: 7, margin: [3,3,3,3] },
              { text: s.chasis || "—",  fontSize: 7, margin: [3,3,3,3] },
              { text: s.fecha ? new Date(s.fecha).toLocaleDateString("es-AR") : "—", fontSize: 7, margin: [3,3,3,3] },
              { text: "Distribuidor:", bold: true, fontSize: 6.5, color: "#fff", fillColor: COL_BG, margin: [3,3,3,3] },
              { text: "Iron Group S.A.", fontSize: 7, margin: [3,3,3,3] },
            ],
          ],
        },
        layout: { hLineWidth: () => 0.3, vLineWidth: () => 0.3, hLineColor: () => "#ccc", vLineColor: () => "#ccc" },
        margin: [0, 0, 0, 5],
      },

      // ── 5. Nota ───────────────────────────────────────────────────────────
      {
        text: "*Nota: A continuación se presentan los elementos relacionados con la operación, mantenimiento y lubricación a Pre-entrega, Entrega Técnica, Servicios periódicos y adicionales. El manual de Operación y mantenimiento detalla todos los elementos para maximizar la vida útil del equipo; algunos artículos pueden ser no aplicables a ciertos modelos.",
        fontSize: 6.5, color: "#555", italics: true, margin: [0, 0, 0, 5],
      },

      // ── 6. Leyenda ────────────────────────────────────────────────────────
      {
        columns: [
          { text: "Ítems a chequear | Resultado del chequeo", bold: true, fontSize: 7.5, margin: [0, 2, 0, 0] },
          { columns: [
              { text: "B", bold: true, fontSize: 7, color: "#fff", background: "#2E7D32", width: 14, margin: [2,1,2,1] },
              { text: " Condición: Buena", fontSize: 7.5, width: "auto", margin: [2,2,0,0] },
            ], width: "auto" },
          { columns: [
              { text: "M", bold: true, fontSize: 7, color: "#fff", background: "#C62828", width: 14, margin: [2,1,2,1] },
              { text: " Condición: Mala", fontSize: 7.5, width: "auto", margin: [2,2,0,0] },
            ], width: "auto" },
          { columns: [
              { text: "E", bold: true, fontSize: 7, color: "#fff", background: ACCENT, width: 14, margin: [2,1,2,1] },
              { text: " Corrección Efectuada", fontSize: 7.5, width: "auto", margin: [2,2,0,0] },
            ], width: "auto" },
        ],
        columnGap: 10,
        margin: [0, 0, 0, 4],
      },

      // ── 7. Tipo de máquina + checklist en dos columnas ────────────────────
      {
        table: {
          widths: ["*"],
          body: [[{
            text: tipoLabel || "—",
            bold: true, fontSize: 9, color: "#fff",
            fillColor: COL_BG, margin: [6, 4, 6, 4],
          }]],
        },
        layout: "noBorders",
        margin: [0, 0, 0, 3],
      },
      condiciones.length > 0
        ? {
            columns: [
              { width: "50%", ...checkTable(keysIzq) },
              { width: "50%", ...checkTable(keysDer) },
            ],
            columnGap: 6,
            margin: [0, 0, 0, 8],
          }
        : { text: "Sin condiciones registradas.", fontSize: 8, color: "#aaa", margin: [0, 0, 0, 8] },

      // ── 8. Comentarios ────────────────────────────────────────────────────
      {
        table: {
          widths: ["*", "*"],
          body: [[
            {
              stack: [
                { text: "Comentarios Cliente:", bold: true, fontSize: 7.5, margin: [0, 0, 0, 3] },
                { text: s.comentarioCliente || "", fontSize: 7.5, color: "#333" },
              ],
              margin: [4, 5, 4, 5],
            },
            {
              stack: [
                { text: "Comentarios del Inspector:", bold: true, fontSize: 7.5, margin: [0, 0, 0, 3] },
                { text: s.comentarioInspector || "", fontSize: 7.5, color: "#333" },
              ],
              margin: [4, 5, 4, 5],
            },
          ]],
        },
        layout: { hLineWidth: () => 0.3, vLineWidth: () => 0.3, hLineColor: () => "#ccc", vLineColor: () => "#ccc" },
        margin: [0, 0, 0, 6],
      },

      // ── 9. Disclaimer ─────────────────────────────────────────────────────
      {
        text: "El Equipo queda en servicio, en condiciones satisfactorias. La información adecuada se ha dado sobre su funcionamiento, cuidado, mantenimiento y operación. Se entrega al cliente los manuales de Partes, Operación y Mantenimiento. Los resultados de la inspección se han explicado.",
        fontSize: 6.5, color: "#555", italics: true, margin: [0, 0, 0, 8],
      },

      // ── 10. Firmas ────────────────────────────────────────────────────────
      {
        table: {
          widths: ["*", "*"],
          body: [[
            {
              stack: [
                ...(s.firmaCliente && s.firmaCliente.startsWith("data:")
                  ? [{ image: s.firmaCliente, width: 140, height: 50, margin: [0, 0, 0, 4] }]
                  : [{ canvas: [{ type: "line", x1: 0, y1: 30, x2: 200, y2: 30, lineWidth: 0.5, lineColor: "#999" }], margin: [0, 0, 0, 4] }]
                ),
                { text: "Firma Cliente", fontSize: 7, color: "#555" },
                { text: `Aclaración: ${s.aclaracionCliente || ""}`, fontSize: 7, color: "#333" },
              ],
              margin: [4, 4, 4, 4],
            },
            {
              stack: [
                ...(s.firmaInspector && s.firmaInspector.startsWith("data:")
                  ? [{ image: s.firmaInspector, width: 140, height: 50, margin: [0, 0, 0, 4] }]
                  : [{ canvas: [{ type: "line", x1: 0, y1: 30, x2: 200, y2: 30, lineWidth: 0.5, lineColor: "#999" }], margin: [0, 0, 0, 4] }]
                ),
                { text: "Firma Inspector", fontSize: 7, color: "#555" },
                { text: `Aclaración: ${s.aclaracionInspector || ""}`, fontSize: 7, color: "#333" },
              ],
              margin: [4, 4, 4, 4],
            },
          ]],
        },
        layout: { hLineWidth: () => 0.3, vLineWidth: () => 0.3, hLineColor: () => "#ccc", vLineColor: () => "#ccc" },
      },
    ],
  };

  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    ? pdfMake.createPdf(doc).download(`reporte-${tipoLabel}-${s.id || ""}.pdf`)
    : pdfMake.createPdf(doc).open();
}
